import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getIdentificationTypes } from '../../api/requests/globalParameters';
import { IdentificationTypeResponse } from '../../types/IdentificationType';
import { createFetchContext } from '../contexts/FetchContextFactory';
import DisplayError from '../info/DisplayError';
import Loading from '../info/Loading';

type IdentificationTypeQuery = {
  countryId?: string | null;
};

interface IdentificationTypeLoaderProps {
  children: ReactElement;
  /**
   * When provided, the loader filters identification types to the global
   * ones (countryId IS NULL) or those issued by the given country. Used by
   * the teacher activation flow to scope to the tenant default country.
   */
  countryId?: string | null;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  IdentificationTypeQuery,
  IdentificationTypeResponse
>();

export function useIdentificationTypeLoader() {
  return useFetch();
}

export const BaseLoaderConsumer = FetchConsumer;

export default function IdentificationTypeLoader({
  children,
  countryId,
}: IdentificationTypeLoaderProps) {
  const { t } = useTranslation();
  const request = useCallback((query: IdentificationTypeQuery) => {
    return getIdentificationTypes(query.countryId);
  }, []);

  const defaultQuery = useMemo<IdentificationTypeQuery>(
    () => ({ countryId: countryId ?? null }),
    [countryId],
  );

  return (
    <FetchProvider
      request={request}
      defaultQuery={defaultQuery}
      fetchImmediately
    >
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return (
              <div className="mx-3">
                <Loading insideCard />
              </div>
            );
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody={t('common:errors.dataNotLoaded')}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children;
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
